import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../index';

// Import the relevant activity models
import type {
  ActivityFilters,
  ActivityResponse,
  ActivityDetailResponse,
  ActivityStreamResponse,
  ActivityListResponse,
  ActivityCreateRequest,
  ActivityUpdateRequest
} from './models/activityModels';

import type {
  TrainingLoadMetrics,
  PerformanceMetrics,
  TrendAnalysis,
  PowerCurveData,
  VO2MaxEstimation,
  LeaderboardEntry,
  UserHeartZonesResponse,
  HRZoneCalculationRequest,
  HRZoneCalculationResponse,
  ManualHRZoneOverride,
  UserPaceZonesResponse,
  PaceZoneCalculationRequest,
  PaceZoneCalculationResponse,
  ManualPaceZoneOverride,
  WorkoutSummaryAnalytics,
  CourseAnalytics,
  WorkoutEquipmentAnalytics,
  WorkoutCompletionAnalytics,
  WorkoutProgressionAnalytics,
} from './models/analyticsModels'

import type {
  UserProfile,
  UpdateUserProfileRequest,
  UpdateNotificationPreferencesRequest,
  RequestRoleChangeRequest,
  EquipmentListResponse,
  VO2maxEstimateResponse,
  LTHREstimateResponse
} from './models/userModels';

// Import the new workout models
import type {
  WorkoutCourseCreate,
  WorkoutCourseUpdate,
  WorkoutCourseResponse,
  WorkoutCreate,
  WorkoutUpdate,
  WorkoutResponse,
  TrainerWorkoutResponse,
  WorkoutSimpleResponse,
  WorkoutCourseSimpleResponse,
  WorkoutSegmentsListResponse,
  WorkoutSegmentDetailResponse,
  ActivityWorkoutLinkResponse
} from './models/workoutModels';

import type {
  DashboardResponse
} from './models/analyticsModels'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

// Stream resolution type
export type StreamResolution = 'raw' | 'filtered' | '1s' | '5s' | '30s';

/**
 * Base API configuration using RTK Query
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: [
    'Users',
    'Activity',
    'ActivityStats',
    'Analytics',
    'Workout',
    'Course',
    'Trainer',
    'Athlete',
    'Notification',
    'SyncStatus',
    'Admin',
    'Zones',
  ],
  endpoints: (builder) => ({
    // ==================== Auth Endpoints ====================
    // (Existing Auth endpoints remain unchanged)
    login: builder.mutation<
      { token: string; user: UserProfile; role: string },
      { code: string; role: string }
    >({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Users'],
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Users'],
    }),

    refreshToken: builder.mutation<{ token: string }, void>({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
    }),

    getCurrentUser: builder.query<UserProfile, void>({
      query: () => '/users/me',
      providesTags: ['Users'],
    }),

    // ==================== Activities Endpoints ====================
    // (Existing Activity endpoints remain unchanged)
    getActivities: builder.query<
      ActivityListResponse,
      ActivityFilters & { user_id?: number }
    >({
      query: (params) => ({
        url: '/activities/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.activities.map(({ id }) => ({
              type: 'Activity' as const,
              id,
            })),
            { type: 'Activity', id: 'LIST' },
          ]
          : [{ type: 'Activity', id: 'LIST' }],
    }),

    getActivityStats: builder.query<
      { [key: string]: any },
      { sport_type?: string; days?: number }
    >({
      query: (params) => ({
        url: '/activities/stats/summary',
        params,
      }),
      providesTags: ['ActivityStats'],
    }),

    getActivityById: builder.query<ActivityDetailResponse, { activity_id: number; include_streams?: boolean; res?: StreamResolution }>({
      query: ({ activity_id, include_streams, res }) => ({
        url: `/activities/${activity_id}`,
        params: {
          ...(include_streams ? { include_streams } : {}),
          ...(res ? { res } : {}),
        },
      }),
      providesTags: (_result, _error, { activity_id }) => [{ type: 'Activity', id: activity_id }],
    }),

    getActivityStreams: builder.query<
      ActivityStreamResponse,
      { activity_id: number; stream_types: string[]; auto_sync?: boolean }
    >({
      query: ({ activity_id, stream_types, auto_sync }) => ({
        url: `/activities/${activity_id}/streams`,
        params: { stream_types: stream_types.join(','), auto_sync },
      }),
      providesTags: (_result, _error, { activity_id }) => [
        { type: 'Activity', id: activity_id },
      ],
    }),

    getActivitySummary: builder.query<ActivityResponse, number>({
      query: (activity_id) => `/activities/${activity_id}/summary`,
      providesTags: (_result, _error, activity_id) => [{ type: 'Activity', id: activity_id }],
    }),

    createActivity: builder.mutation<ActivityDetailResponse, ActivityCreateRequest>({
      query: (body) => ({
        url: '/activities',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Activity', id: 'LIST' }],
    }),

    updateActivity: builder.mutation<ActivityDetailResponse, { activity_id: number; data: ActivityUpdateRequest }>({
      query: ({ activity_id, data }) => ({
        url: `/activities/${activity_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { activity_id }) => [
        { type: 'Activity', id: activity_id },
        { type: 'Activity', id: 'LIST' },
      ],
    }),

    deleteActivity: builder.mutation<void, number>({
      query: (activity_id) => ({
        url: `/activities/${activity_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, activity_id) => [
        { type: 'Activity', id: activity_id },
        { type: 'Activity', id: 'LIST' },
      ],
    }),

    linkActivityToWorkout: builder.mutation<
      void,
      { activityId: string; workout_id: string }
    >({
      query: ({ activityId, workout_id }) => ({
        url: `/activities/${activityId}/link-workout`,
        method: 'PATCH',
        body: { workout_id },
      }),
      invalidatesTags: (_result, _error, { activityId, workout_id }) => [
        { type: 'Activity', id: activityId },
        { type: 'Workout', id: workout_id },
      ],
    }),

    unlinkActivityFromWorkout: builder.mutation<
      void,
      { activityId: string }
    >({
      query: ({ activityId }) => ({
        url: `/activities/${activityId}/unlink-workout`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { activityId }) => [
        { type: 'Activity', id: activityId },
        { type: 'Workout', id: 'LIST' },
        { type: 'Workout', id: 'SIMPLE_LIST' },
      ],
    }),

    getActivityWorkoutLink: builder.query<
      ActivityWorkoutLinkResponse,
      number
    >({
      query: (activityId) => `/activities/${activityId}/workout`,
      providesTags: (_result, _error, activityId) => [
        { type: 'Activity', id: activityId },
      ],
    }),

    // ==================== Analytics Endpoints ====================

    getAnalyticsDashboard: builder.query<
      DashboardResponse,
      { days?: number }
    >({
      query: ({ days = 90 }) => ({
        url: '/analytics/dashboard',
        params: { days },
      }),
      providesTags: ['Analytics'],
    }),

    getTrainingLoad: builder.query<
      TrainingLoadMetrics,
      { start_date: string; end_date: string; sport_type?: string }
    >({
      query: ({ start_date, end_date, sport_type }) => ({
        url: '/analytics/training-load',
        params: { start_date, end_date, sport_type },
      }),
      providesTags: ['Analytics'],
    }),

    getPerformanceMetrics: builder.query<
      PerformanceMetrics,
      { sport_type?: 'Run' | 'Ride'; start_date?: string; end_date?: string }
    >({
      query: (params) => ({
        url: '/analytics/performance',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    getTrendAnalysis: builder.query<
      TrendAnalysis,
      { start_date: string; end_date: string; sport_type?: 'Run' | 'Ride' }
    >({
      query: ({ start_date, end_date, sport_type }) => ({
        url: '/analytics/trends',
        params: { start_date, end_date, sport_type },
      }),
      providesTags: ['Analytics'],
    }),

    getPowerCurves: builder.query<
      PowerCurveData,
      { sport_type?: string; days_back?: number }
    >({
      query: ({ sport_type = 'Ride', days_back = 365 }) => ({
        url: '/analytics/power-curves',
        params: { sport_type, days_back },
      }),
      providesTags: ['Analytics'],
    }),

    getVO2Estimation: builder.query<
      VO2MaxEstimation,
      { days_back?: number }
    >({
      query: ({ days_back = 180 }) => ({
        url: '/analytics/vo2-estimation',
        params: { days_back },
      }),
      providesTags: ['Analytics'],
    }),

    getLeaderboard: builder.query<
      LeaderboardEntry[],
      { metric_name: string; time_period?: string; sport_type?: 'Run' | 'Ride'; limit?: number }
    >({
      query: ({ metric_name, time_period = 'monthly', sport_type, limit = 50 }) => ({
        url: `/analytics/leaderboards/${metric_name}`,
        params: { time_period, sport_type, limit },
      }),
      providesTags: ['Analytics'],
    }),

    // ==================== Zones Endpoints ====================

    calculateHeartRateZones: builder.mutation<
      HRZoneCalculationResponse,
      HRZoneCalculationRequest
    >({
      query: (body) => ({
        url: '/analytics/hr-zones/calc',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Zones', 'Analytics'],
    }),

    getHeartRateZones: builder.query<UserHeartZonesResponse | null, void>({
      query: () => '/analytics/hr-zones',
      providesTags: ['Zones'],
    }),

    setManualHeartRateZones: builder.mutation<
      UserHeartZonesResponse,
      ManualHRZoneOverride
    >({
      query: (body) => ({
        url: '/analytics/hr-zones',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Zones', 'Analytics'],
    }),

    calculatePaceZones: builder.mutation<
      PaceZoneCalculationResponse,
      PaceZoneCalculationRequest
    >({
      query: (body) => ({
        url: '/analytics/pace-zones/calc',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Zones', 'Analytics'],
    }),

    getPaceZones: builder.query<UserPaceZonesResponse | null, void>({
      query: () => '/analytics/pace-zones',
      providesTags: ['Zones'],
    }),

    setManualPaceZones: builder.mutation<
      UserPaceZonesResponse,
      ManualPaceZoneOverride
    >({
      query: (body) => ({
        url: '/analytics/pace-zones',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Zones', 'Analytics'],
    }),

    // ==================== Trainer Analytics Endpoints ====================

    getTrainerAthleteDashboard: builder.query<
      DashboardResponse,
      { athlete_id: number; days?: number }
    >({
      query: ({ athlete_id, days = 90 }) => ({
        url: `/analytics/trainer/athletes/${athlete_id}/dashboard`,
        params: { days },
      }),
      providesTags: (result, error, { athlete_id }) => [
        { type: 'Analytics', id: `ATHLETE-${athlete_id}` },
        { type: 'Athlete', id: athlete_id },
      ],
    }),

    getTrainerAthleteTrainingLoad: builder.query<
      TrainingLoadMetrics,
      { athlete_id: number; start_date: string; end_date: string; sport_type?: string }
    >({
      query: ({ athlete_id, ...params }) => ({
        url: `/analytics/trainer/athletes/${athlete_id}/training-load`,
        params,
      }),
      providesTags: (result, error, { athlete_id }) => [
        { type: 'Analytics', id: `ATHLETE-${athlete_id}` },
      ],
    }),

    getTrainerOverview: builder.query<
      any, // dict response as per python
      { start_date?: string; end_date?: string }
    >({
      query: (params) => ({
        url: '/analytics/trainer/overview',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    // ==================== Workout Course Endpoints ====================

    createWorkoutCourse: builder.mutation<
      WorkoutCourseResponse,
      WorkoutCourseCreate
    >({
      query: (body) => ({
        url: '/workouts/courses',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Course', id: 'LIST' }],
    }),

    getWorkoutCourse: builder.query<
      WorkoutCourseResponse,
      number
    >({
      query: (course_id) => `/workouts/courses/${course_id}`,
      providesTags: (_result, _error, course_id) => [{ type: 'Course', id: course_id }],
    }),

    updateWorkoutCourse: builder.mutation<
      WorkoutCourseResponse,
      { course_id: number; data: WorkoutCourseUpdate }
    >({
      query: ({ course_id, data }) => ({
        url: `/workouts/courses/${course_id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { course_id }) => [
        { type: 'Course', id: course_id },
        { type: 'Course', id: 'LIST' },
      ],
    }),

    deleteWorkoutCourse: builder.mutation<
      void,
      number
    >({
      query: (course_id) => ({
        url: `/workouts/courses/${course_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, course_id) => [
        { type: 'Course', id: course_id },
        { type: 'Course', id: 'LIST' },
      ],
    }),

    listWorkoutCourses: builder.query<
      WorkoutCourseResponse[],
      { skip?: number; limit?: number }
    >({
      query: ({ skip = 0, limit = 100 }) => ({
        url: '/workouts/courses',
        params: { skip, limit },
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Course' as const, id })),
            { type: 'Course', id: 'LIST' },
          ]
          : [{ type: 'Course', id: 'LIST' }],
    }),

    getSimpleWorkoutCourses: builder.query<
      WorkoutCourseSimpleResponse[],
      void // No query parameters
    >({
      query: () => '/workouts/simple/courses',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Course' as const, id })),
            { type: 'Course', id: 'SIMPLE_LIST' },
          ]
          : [{ type: 'Course', id: 'SIMPLE_LIST' }],
    }),


    // ==================== Workout Instance Endpoints ====================

    listWorkouts: builder.query<
      WorkoutResponse[],
      {
        athlete_id?: number;
        trainer_id?: number;
        status?: string;
        start_date?: string;
        end_date?: string;
        priority?: string;
        course_id?: number;
        page?: number;
        per_page?: number;
      }
    >({
      query: (params) => ({
        url: '/workouts',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Workout' as const, id })),
            { type: 'Workout', id: 'LIST' },
          ]
          : [{ type: 'Workout', id: 'LIST' }],
    }),

    getSimpleWorkouts: builder.query<
      WorkoutSimpleResponse[],
      { athlete_id?: number }
    >({
      query: (params) => ({
        url: '/workouts/simple',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Workout' as const, id })),
            { type: 'Workout', id: 'SIMPLE_LIST' },
          ]
          : [{ type: 'Workout', id: 'SIMPLE_LIST' }],
    }),

    getUpcomingWorkouts: builder.query<
      WorkoutResponse[],
      { limit?: number; days_ahead?: number; athlete_id?: number }
    >({
      query: ({ limit = 10, days_ahead = 7, athlete_id }) => ({
        url: '/workouts/upcoming',
        params: { limit, days_ahead, athlete_id },
      }),
      providesTags: ['Workout'],
    }),

    getTrainerRecentWorkouts: builder.query<
      TrainerWorkoutResponse[],
      { limit?: number }
    >({
      query: ({ limit = 10 }) => ({
        url: '/workouts/trainer/recent',
        params: { limit },
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Workout' as const, id })),
            { type: 'Workout', id: 'LIST' },
          ]
          : [{ type: 'Workout', id: 'LIST' }],
    }),

    createWorkout: builder.mutation<
      WorkoutResponse,
      WorkoutCreate
    >({
      query: (body) => ({
        url: '/workouts',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Workout', id: 'LIST' },
        { type: 'Workout', id: 'SIMPLE_LIST' },
      ],
    }),

    getWorkoutById: builder.query<
      WorkoutResponse,
      string
    >({
      query: (id) => `/workouts/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Workout', id }],
    }),

    updateWorkout: builder.mutation<
      WorkoutResponse,
      { id: string; data: WorkoutUpdate }
    >({
      query: ({ id, data }) => ({
        url: `/workouts/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Workout', id },
        { type: 'Workout', id: 'LIST' },
        { type: 'Workout', id: 'SIMPLE_LIST' },
      ],
    }),

    // DEPRECATED: Kept for backward compatibility, use updateWorkout
    updateWorkoutStatus: builder.mutation<
      any,
      { id: string; status: string; notes?: string }
    >({
      query: ({ id, status, notes }) => ({
        url: `/workouts/${id}/status`,
        method: 'PATCH',
        body: { status, notes },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Workout', id },
        { type: 'Workout', id: 'LIST' },
        { type: 'Workout', id: 'SIMPLE_LIST' },
      ],
    }),

    // ==================== Workout Segment Endpoints ====================

    getWorkoutSegments: builder.query<
      WorkoutSegmentsListResponse,
      { workoutId: number; activityId?: number }
    >({
      query: ({ workoutId, activityId }) => ({
        url: `/workouts/${workoutId}/segments`,
        params: activityId ? { activity_id: activityId } : undefined,
      }),
      providesTags: (_result, _error, { workoutId }) => [
        { type: 'Workout', id: workoutId },
        { type: 'Analytics', id: `WORKOUT-${workoutId}` },
      ],
    }),

    getSegmentDetail: builder.query<
      WorkoutSegmentDetailResponse,
      { segmentId: number; res?: StreamResolution[]; sampling_rate?: number }
    >({
      query: ({ segmentId, res, sampling_rate }) => ({
        url: `/segments/${segmentId}`,
        params: {
          ...(res && res.length > 0 ? { res: res.join(',') } : {}),
          ...(sampling_rate ? { sampling_rate } : {}),
        },
      }),
      providesTags: (_result, _error, { segmentId }) => [
        { type: 'Analytics', id: `SEGMENT-${segmentId}` },
      ],
    }),

    // ==================== Workout Analytics Endpoints ====================

    getWorkoutSummary: builder.query<
      WorkoutSummaryAnalytics,
      { days?: number; course_id?: number; workout_id?: number }
    >({
      query: (params) => ({
        url: '/analytics/workouts/summary',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    getCourseAnalytics: builder.query<
      CourseAnalytics,
      number
    >({
      query: (course_id) => `/analytics/workouts/courses/${course_id}/stats`,
      providesTags: (_result, _error, course_id) => [
        { type: 'Analytics', id: `COURSE-${course_id}` },
        { type: 'Course', id: course_id },
      ],
    }),

    getWorkoutEquipmentAnalytics: builder.query<
      WorkoutEquipmentAnalytics,
      { days?: number }
    >({
      query: (params) => ({
        url: '/analytics/workouts/equipment',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    getWorkoutCompletionAnalytics: builder.query<
      WorkoutCompletionAnalytics,
      { days?: number }
    >({
      query: (params) => ({
        url: '/analytics/workouts/completion',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    getWorkoutProgressionAnalytics: builder.query<
      WorkoutProgressionAnalytics,
      { metric: string; course_id?: number; workout_id?: number; days?: number }
    >({
      query: (params) => ({
        url: '/analytics/workouts/progression',
        params,
      }),
      providesTags: ['Analytics'],
    }),

    // ==================== Courses (Templates) Legacy Endpoints ====================
    // (Preserving legacy endpoints if needed, otherwise mapped to new structure)
    getCourses: builder.query<
      any,
      { sportType?: string; search?: string; page?: number; pageSize?: number }
    >({
      query: (params) => ({
        url: '/courses', // Assuming this maps to /workouts/courses or is a separate legacy route
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.courses.map(({ id }: any) => ({
              type: 'Course' as const,
              id,
            })),
            { type: 'Course', id: 'LIST' },
          ]
          : [{ type: 'Course', id: 'LIST' }],
    }),

    getCourseById: builder.query<any, string>({
      query: (id) => `/courses/${id}`, // Maps to /workouts/courses/{id}
      providesTags: (_result, _error, id) => [{ type: 'Course', id }],
    }),

    createCourse: builder.mutation<any, any>({
      query: (body) => ({
        url: '/courses', // Maps to /workouts/courses
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Course', id: 'LIST' }],
    }),

    updateCourse: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/courses/${id}`, // Maps to /workouts/courses/{id}
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Course', id },
        { type: 'Course', id: 'LIST' },
      ],
    }),

    deleteCourse: builder.mutation<void, string>({
      query: (id) => ({
        url: `/courses/${id}`, // Maps to /workouts/courses/{id}
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Course', id },
        { type: 'Course', id: 'LIST' },
      ],
    }),

    // ==================== Trainer Endpoints ====================
    // (Existing Trainer endpoints remain unchanged)
    getTrainerAthletes: builder.query<
      any,
      { consentStatus?: string; search?: string }
    >({
      query: (params) => ({
        url: '/trainer/athletes',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.athletes.map(({ id }: any) => ({
              type: 'Athlete' as const,
              id,
            })),
            { type: 'Athlete', id: 'LIST' },
          ]
          : [{ type: 'Athlete', id: 'LIST' }],
    }),

    getAthleteProfile: builder.query<any, string>({
      query: (athleteId) => `/trainer/athletes/${athleteId}`,
      providesTags: (_result, _error, athleteId) => [
        { type: 'Athlete', id: athleteId },
      ],
    }),

    getAthleteAnalytics: builder.query<
      any,
      { athleteId: string; startDate?: string; endDate?: string }
    >({
      query: ({ athleteId, ...params }) => ({
        url: `/trainer/athletes/${athleteId}/analytics`,
        params,
      }),
      providesTags: (_result, _error, { athleteId }) => [
        { type: 'Athlete', id: athleteId },
        'Analytics',
      ],
    }),

    addAthlete: builder.mutation<any, { email: string; message?: string }>({
      query: (body) => ({
        url: '/trainer/athletes',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Athlete', id: 'LIST' }],
    }),

    updateAthleteNotes: builder.mutation<
      any,
      { athleteId: string; notes: string }
    >({
      query: ({ athleteId, notes }) => ({
        url: `/trainer/athletes/${athleteId}/notes`,
        method: 'PATCH',
        body: { notes },
      }),
      invalidatesTags: (_result, _error, { athleteId }) => [
        { type: 'Athlete', id: athleteId },
      ],
    }),

    assignWorkout: builder.mutation<
      any,
      {
        athleteId: string;
        courseId?: string;
        scheduledDate: string;
        priority?: string;
        notes?: string;
        structure?: any;
        repeat?: any;
      }
    >({
      query: (body) => ({
        url: '/trainer/assign-workout',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Workout', id: 'LIST' },
        { type: 'Workout', id: 'SIMPLE_LIST' },
        { type: 'Athlete', id: 'LIST' },
      ],
    }),

    // ==================== Athlete Trainer Management ====================
    // (Existing Athlete Trainer endpoints remain unchanged)
    getMyTrainers: builder.query<any, void>({
      query: () => '/athlete/trainers',
      providesTags: (result) =>
        result
          ? [
            ...result.trainers.map(({ id }: any) => ({
              type: 'Trainer' as const,
              id,
            })),
            { type: 'Trainer', id: 'LIST' },
          ]
          : [{ type: 'Trainer', id: 'LIST' }],
    }),

    requestTrainer: builder.mutation<
      any,
      { trainerEmail: string; message?: string }
    >({
      query: (body) => ({
        url: '/athlete/trainers/request',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Trainer', id: 'LIST' }],
    }),

    updateTrainerConsent: builder.mutation<
      any,
      { trainerId: string; consentStatus: string }
    >({
      query: ({ trainerId, consentStatus }) => ({
        url: `/athlete/trainers/${trainerId}/consent`,
        method: 'PATCH',
        body: { consentStatus },
      }),
      invalidatesTags: (_result, _error, { trainerId }) => [
        { type: 'Trainer', id: trainerId },
        { type: 'Trainer', id: 'LIST' },
      ],
    }),

    removeTrainer: builder.mutation<any, string>({
      query: (trainerId) => ({
        url: `/athlete/trainers/${trainerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, trainerId) => [
        { type: 'Trainer', id: trainerId },
        { type: 'Trainer', id: 'LIST' },
      ],
    }),

    // ==================== Sync Endpoints ====================
    // (Existing Sync endpoints remain unchanged)
    getSyncStatus: builder.query<any, void>({
      query: () => '/sync/status',
      providesTags: ['SyncStatus'],
    }),

    getSyncHistory: builder.query<any, { page?: number; pageSize?: number }>({
      query: (params) => ({
        url: '/sync/history',
        params,
      }),
      providesTags: ['SyncStatus'],
    }),

    triggerSync: builder.mutation<any, void>({
      query: () => ({
        url: '/sync/trigger',
        method: 'POST',
      }),
      invalidatesTags: ['SyncStatus', { type: 'Activity', id: 'LIST' }],
    }),

    // ==================== Notifications Endpoints ====================
    // (Existing Notifications endpoints remain unchanged)
    getNotifications: builder.query<
      any,
      { unreadOnly?: boolean; page?: number; pageSize?: number }
    >({
      query: (params) => ({
        url: '/notifications',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.notifications.map(({ id }: any) => ({
              type: 'Notification' as const,
              id,
            })),
            { type: 'Notification', id: 'LIST' },
          ]
          : [{ type: 'Notification', id: 'LIST' }],
    }),

    markNotificationRead: builder.mutation<any, string>({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
      ],
    }),

    markAllNotificationsRead: builder.mutation<any, void>({
      query: () => ({
        url: '/notifications/read-all',
        method: 'PATCH',
      }),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
    }),

    deleteNotification: builder.mutation<void, string>({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Notification', id },
        { type: 'Notification', id: 'LIST' },
      ],
    }),

    subscribeToPushNotifications: builder.mutation<
      any,
      { subscription: PushSubscription }
    >({
      query: (body) => ({
        url: '/notifications/subscribe',
        method: 'POST',
        body,
      }),
    }),

    // ==================== User Settings Endpoints ====================
    // (Existing User Settings endpoints remain unchanged)
    updateUserProfile: builder.mutation<UserProfile, UpdateUserProfileRequest>({
      query: (body) => ({
        url: '/users/me', // Updated to /users/me based on standard patterns, original was /users/profile
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    estimateVO2max: builder.query<VO2maxEstimateResponse, { max_hr: number; age: number; resting_hr: number }>({
      query: (params) => ({
        url: '/users/me/vo2max/estimate',
        params,
      }),
    }),

    estimateLTHR: builder.query<LTHREstimateResponse, { max_hr: number }>({
      query: (params) => ({
        url: '/users/me/lthr/estimate',
        params,
      }),
    }),


    updateNotificationPreferences: builder.mutation<UserProfile, UpdateNotificationPreferencesRequest>({
      query: (body) => ({
        url: '/users/notification-preferences',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    requestRoleChange: builder.mutation<UserProfile, RequestRoleChangeRequest>({
      query: (body) => ({
        url: '/users/request-role-change',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),

    deleteAccount: builder.mutation<void, void>({
      query: () => ({
        url: '/users/account',
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    // ==================== Equipment Endpoints ====================

    getUserEquipment: builder.query<EquipmentListResponse, void>({
      query: () => '/users/me/equipment',
      providesTags: ['Users'],
    }),

    // ==================== Admin Endpoints ====================
    // (Existing Admin endpoints remain unchanged)
    getAdminDashboard: builder.query<any, void>({
      query: () => '/admin/dashboard',
      providesTags: ['Admin'],
    }),

    getSystemHealth: builder.query<any, void>({
      query: () => '/admin/health',
      providesTags: ['Admin'],
    }),

    getUsers: builder.query<
      any,
      {
        role?: string;
        active?: boolean;
        stravaConnected?: boolean;
        search?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: (params) => ({
        url: '/admin/users',
        params,
      }),
      providesTags: ['Admin'],
    }),

    updateUserRole: builder.mutation<any, { userId: string; role: string }>({
      query: ({ userId, role }) => ({
        url: `/admin/users/${userId}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: ['Admin'],
    }),

    suspendUser: builder.mutation<any, { userId: string; reason?: string }>({
      query: ({ userId, reason }) => ({
        url: `/admin/users/${userId}/suspend`,
        method: 'PATCH',
        body: { reason },
      }),
      invalidatesTags: ['Admin'],
    }),

    activateUser: builder.mutation<any, string>({
      query: (userId) => ({
        url: `/admin/users/${userId}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Admin'],
    }),

    deleteUser: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Admin'],
    }),

    getShards: builder.query<any, void>({
      query: () => '/admin/shards',
      providesTags: ['Admin'],
    }),

    checkShardHealth: builder.mutation<any, string>({
      query: (shardId) => ({
        url: `/admin/shards/${shardId}/health`,
        method: 'POST',
      }),
      invalidatesTags: ['Admin'],
    }),

    rebalanceShard: builder.mutation<any, string>({
      query: (shardId) => ({
        url: `/admin/shards/${shardId}/rebalance`,
        method: 'POST',
      }),
      invalidatesTags: ['Admin'],
    }),

    getFeatureFlags: builder.query<any, void>({
      query: () => '/admin/feature-flags',
      providesTags: ['Admin'],
    }),

    updateFeatureFlag: builder.mutation<
      any,
      { flagName: string; enabled: boolean }
    >({
      query: ({ flagName, enabled }) => ({
        url: `/admin/feature-flags/${flagName}`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['Admin'],
    }),

    updateSystemConfig: builder.mutation<any, any>({
      query: (body) => ({
        url: '/admin/config',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Admin'],
    }),

    clearCache: builder.mutation<any, { cacheType?: string }>({
      query: (body) => ({
        url: '/admin/cache/clear',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Admin'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Auth
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useGetCurrentUserQuery,

  // Activities
  useGetActivitiesQuery,
  useGetActivityStatsQuery,
  useGetActivityByIdQuery,
  useGetActivityStreamsQuery,
  useGetActivitySummaryQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useLinkActivityToWorkoutMutation,
  useUnlinkActivityFromWorkoutMutation,
  useGetActivityWorkoutLinkQuery,

  // Analytics
  useGetAnalyticsDashboardQuery,
  useGetTrainingLoadQuery,
  useGetPerformanceMetricsQuery,
  useGetTrendAnalysisQuery,
  useGetPowerCurvesQuery,
  useGetVO2EstimationQuery,
  useGetLeaderboardQuery,

  // Zones
  useCalculateHeartRateZonesMutation,
  useGetHeartRateZonesQuery,
  useSetManualHeartRateZonesMutation,
  useCalculatePaceZonesMutation,
  useGetPaceZonesQuery,
  useSetManualPaceZonesMutation,

  // Trainer Analytics
  useGetTrainerAthleteDashboardQuery,
  useGetTrainerAthleteTrainingLoadQuery,
  useGetTrainerOverviewQuery,

  // Workout Courses
  useCreateWorkoutCourseMutation,
  useGetWorkoutCourseQuery,
  useUpdateWorkoutCourseMutation,
  useDeleteWorkoutCourseMutation,
  useListWorkoutCoursesQuery,
  useGetSimpleWorkoutCoursesQuery,

  // Workout Instances
  useListWorkoutsQuery,
  useGetSimpleWorkoutsQuery,
  useGetUpcomingWorkoutsQuery,
  useGetTrainerRecentWorkoutsQuery,
  useCreateWorkoutMutation,
  useGetWorkoutByIdQuery,
  useUpdateWorkoutMutation,
  useUpdateWorkoutStatusMutation,

  // Workout Segments
  useGetWorkoutSegmentsQuery,
  useGetSegmentDetailQuery,

  // Workout Analytics
  useGetWorkoutSummaryQuery,
  useGetCourseAnalyticsQuery,
  useGetWorkoutEquipmentAnalyticsQuery,
  useGetWorkoutCompletionAnalyticsQuery,
  useGetWorkoutProgressionAnalyticsQuery,

  // Courses (Legacy)
  useGetCoursesQuery,
  useGetCourseByIdQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,

  // Trainer
  useGetTrainerAthletesQuery,
  useGetAthleteProfileQuery,
  useGetAthleteAnalyticsQuery,
  useAddAthleteMutation,
  useUpdateAthleteNotesMutation,
  useAssignWorkoutMutation,

  // Athlete Trainer Management
  useGetMyTrainersQuery,
  useRequestTrainerMutation,
  useUpdateTrainerConsentMutation,
  useRemoveTrainerMutation,

  // Sync
  useGetSyncStatusQuery,
  useGetSyncHistoryQuery,
  useTriggerSyncMutation,

  // Notifications
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
  useSubscribeToPushNotificationsMutation,

  // User Settings
  useUpdateUserProfileMutation,
  useUpdateNotificationPreferencesMutation,
  useRequestRoleChangeMutation,
  useDeleteAccountMutation,
  useEstimateVO2maxQuery,
  useEstimateLTHRQuery,

  // Equipment
  useGetUserEquipmentQuery,

  // Admin
  useGetAdminDashboardQuery,
  useGetSystemHealthQuery,
  useGetUsersQuery,
  useUpdateUserRoleMutation,
  useSuspendUserMutation,
  useActivateUserMutation,
  useDeleteUserMutation,
  useGetShardsQuery,
  useCheckShardHealthMutation,
  useRebalanceShardMutation,
  useGetFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,
  useUpdateSystemConfigMutation,
  useClearCacheMutation,
} = baseApi;
